import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import {
    ConfigurationDataResultModel,
    ConfiguratorItemAttributesModel,
    ConfiguratorProductModel,
} from "@tSurya/Store/Data/ConfiguratorModels";
import { ConfiguratorState, configuratorInitialState } from "@tSurya/Store/Data/ConfiguratorState";
import { Draft } from "immer";

const reducer = {
    "Custom/Configurator/Initialize": (
        draft: Draft<ConfiguratorState>,
        action: {
            productConfig: ConfiguratorProductModel;
        },
    ) => {
        draft.productConfig = action.productConfig;
        draft.selections = {};
        // build a lightweight lookup from valueId -> value for fast resolution in selectors
        const lookup: Record<string, string> = {};
        const opts = action.productConfig?.options ?? [];
        for (const opt of opts) {
            const entries = opt.optionGroup?.entries ?? [];
            for (const entry of entries) {
                const vals = entry.values ?? [];
                for (const v of vals) {
                    if (v?.valueId) {
                        lookup[v.valueId] = v.value || "";
                    }
                }
            }
        }
        draft.valueLookup = lookup;
        draft.configurationResult = null;
        draft.cylindoConfig = null;
        draft.validatedCylindoFeatures = null;
        draft.selectedStyleId = null;
        draft.itemAttributes = null;
        draft.baselineProductProperties = null;
        draft.isUpdating = false;
        draft.error = null;
    },
    "Custom/Configurator/BeginUpdate": (draft: Draft<ConfiguratorState>) => {
        draft.isUpdating = true;
        draft.error = null;
    },
    "Custom/Configurator/CompleteUpdate": (
        draft: Draft<ConfiguratorState>,
        action: {
            selections: Record<string, string>;
            configurationResult: ConfigurationDataResultModel;
        },
    ) => {
        draft.selections = action.configurationResult.selections;
        draft.configurationResult = action.configurationResult;
        draft.cylindoConfig = action.configurationResult.cylindoConfig;
        draft.isUpdating = false;
        draft.error = null;
    },
    "Custom/Configurator/SetSelectedStyleId": (
        draft: Draft<ConfiguratorState>,
        action: { selectedStyleId: string | null },
    ) => {
        draft.selectedStyleId = action.selectedStyleId;
    },
    "Custom/Configurator/SetValidatedCylindoFeatures": (
        draft: Draft<ConfiguratorState>,
        action: { validatedCylindoFeatures: Record<string, string> | null; droppedCylindoFeatures: string[] | null },
    ) => {
        draft.validatedCylindoFeatures = action.validatedCylindoFeatures;
        draft.droppedCylindoFeatures = action.droppedCylindoFeatures;
    },
    "Custom/Configurator/SetItemAttributes": (
        draft: Draft<ConfiguratorState>,
        action: { itemAttributes: ConfiguratorItemAttributesModel[] | null },
    ) => {
        draft.itemAttributes = action.itemAttributes;
    },
    "Custom/Configurator/SetBaselineProductProperties": (
        draft: Draft<ConfiguratorState>,
        action: { baselineProductProperties: Record<string, string> | null },
    ) => {
        draft.baselineProductProperties = action.baselineProductProperties;
    },
    "Custom/Configurator/FailUpdate": (
        draft: Draft<ConfiguratorState>,
        action: { error: string },
    ) => {
        draft.isUpdating = false;
        draft.error = action.error;
    },
    "Custom/Configurator/Reset": (draft: Draft<ConfiguratorState>) => {
        draft.productConfig = configuratorInitialState.productConfig;
        draft.selections = configuratorInitialState.selections;
        draft.valueLookup = configuratorInitialState.valueLookup;
        draft.configurationResult = configuratorInitialState.configurationResult;
        draft.cylindoConfig = configuratorInitialState.cylindoConfig;
        draft.validatedCylindoFeatures = configuratorInitialState.validatedCylindoFeatures;
        draft.selectedStyleId = configuratorInitialState.selectedStyleId;
        draft.itemAttributes = configuratorInitialState.itemAttributes;
        draft.baselineProductProperties = configuratorInitialState.baselineProductProperties;
        draft.isUpdating = configuratorInitialState.isUpdating;
        draft.error = configuratorInitialState.error;
    },
};

export default createTypedReducerWithImmer(configuratorInitialState, reducer);
